import { useMemo, useState } from "react";
import piAPI from "../../../../api/inventory/piApi";
import { showError, showSuccess } from "../../../../utils/toastUtils";
import LocationSelect from "../common/LocationSelect";
import ProductSKUSearch from "../common/ProductSKUSearch";
import SupplierSelect from "../common/SupplierSelect";
import { Link } from "react-router-dom";

export default function PurchaseInvoiceTab({
  defaultLocationId,
  onSuccess,
  onOpenFullPage,
}) {
  const INITIAL_FORM_DATA = {
    supplierId: "",
    locationId: defaultLocationId || "",
    invoiceNumber: "",
    invoiceDate: new Date().toISOString().slice(0, 10),
    dueDate: "",
    items: [],
    discount: 0,
    shipping: 0,
    notes: "",
    action: "receive",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  /***** state change  *****/
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const excludeIds = useMemo(
    () => formData.items.map((i) => i.inventoryItemId),
    [formData.items],
  );

  const addItem = (product) => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          inventoryItemId: product.inventoryItemId,
          sku: product.sku,
          productName: product.productName,
          variantName: product.variantName,
          quantity: 1,
          unitCost: product.lastCost ? Number(product.lastCost) : 0,
          taxRate: formData.supplier?.defaultTaxRate
            ? Number(formData.supplier.defaultTaxRate)
            : 18,
          discountPercent: 0,
        },
      ],
    }));
  };

  const updateLine = (idx, field, value) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === idx ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const removeLine = (idx) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx),
    }));
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    let taxAmount = 0;

    formData.items.forEach((i) => {
      const lineSubtotal =
        (parseFloat(i.quantity) || 0) * (parseFloat(i.unitCost) || 0);
      const lineDiscount =
        lineSubtotal * ((parseFloat(i.discountPercent) || 0) / 100);
      const lineTax =
        (lineSubtotal - lineDiscount) * ((parseFloat(i.taxRate) || 0) / 100);
      subtotal += lineSubtotal - lineDiscount;
      taxAmount += lineTax;
    });

    const grandTotal =
      subtotal +
      taxAmount -
      (parseFloat(formData.discount) || 0) +
      (parseFloat(formData.shipping) || 0);
    return { subtotal, taxAmount, grandTotal };
  }, [formData.items, formData.discount, formData.shipping]);

  const canSave =
    formData.supplierId &&
    formData.locationId &&
    formData.invoiceNumber &&
    formData.invoiceDate &&
    formData.items.length > 0 &&
    formData.items.every(
      (i) => parseInt(i.quantity, 10) > 0 && parseFloat(i.unitCost) >= 0,
    );

  const handleSave = async () => {
    if (!canSave) return;

    setLoading(true);

    try {
      const payload = {
        ...formData,
        items: formData.items.map((i) => ({
          inventoryItemId: i.inventoryItemId,
          quantity: parseInt(i.quantity, 10),
          unitCost: parseFloat(i.unitCost),
          taxRate: parseFloat(i.taxRate || 0),
          discountPercent: parseFloat(i.discountPercent || 0),
        })),
      };

      const result = await piAPI.create(payload);

      showSuccess(
        formData.action === "receive"
          ? `PI ${formData.invoiceNumber} created & stock received`
          : `PI ${formData.invoiceNumber} saved as draft`,
      );
      onSuccess?.(result);
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to save PI";
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-end mb-2">
        <Link
          type="button"
          className="btn btn-sm btn-link"
          to="/purchase-invoices/add-new"
        >
          Open full PI page <i className="ti ti-arrow-right ms-1" />
        </Link>
      </div>

      {/* Supplier + Location */}
      <div className="row g-2 mb-3">
        <div className="col-md-7">
          <label className="form-label small">Supplier *</label>
          <SupplierSelect
            name="supplierId"
            value={formData.supplierId}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-5">
          <label className="form-label small">Receive at Location *</label>
          <LocationSelect
            value={formData.locationId}
            onChange={handleChange}
            name="locationId"
            required
          />
        </div>
      </div>

      {/* Invoice meta */}
      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <label className="form-label small">Invoice Number *</label>
          <input
            type="text"
            className="form-control"
            value={formData.invoiceNumber}
            onChange={handleChange}
            name="invoiceNumber"
            placeholder="e.g. INV-12345"
          />
        </div>
        <div className="col-md-4">
          <label className="form-label small">Invoice Date *</label>
          <input
            type="date"
            className="form-control"
            value={formData.invoiceDate}
            name="invoiceDate"
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label small">Due Date</label>
          <input
            type="date"
            className="form-control"
            value={formData.dueDate}
            name="dueDate"
            onChange={handleChange}
          />
        </div>
      </div>

      {/* Items */}
      <div className="mb-3">
        <label className="form-label small">Add Items *</label>
        <ProductSKUSearch
          onSelect={addItem}
          excludeIds={excludeIds}
          locationId={formData.locationId}
        />
      </div>

      {formData.items.length > 0 && (
        <div className="table-responsive mb-3">
          <table className="table table-sm align-middle">
            <thead className="table-light">
              <tr>
                <th>Item</th>
                <th style={{ width: 80 }}>Qty</th>
                <th style={{ width: 100 }}>Unit Cost</th>
                <th style={{ width: 80 }}>Tax %</th>
                <th style={{ width: 100 }}>Line Total</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {formData.items.map((line, idx) => {
                const lineSubtotal =
                  (parseFloat(line.quantity) || 0) *
                  (parseFloat(line.unitCost) || 0);
                const lineTax =
                  lineSubtotal * ((parseFloat(line.taxRate) || 0) / 100);
                const lineTotal = lineSubtotal + lineTax;
                return (
                  <tr key={line.inventoryItemId}>
                    <td>
                      <div className="fw-semibold small">
                        {line.productName}
                      </div>
                      <div className="text-muted small">
                        {line.variantName} · <code>{line.sku}</code>
                      </div>
                    </td>
                    <td>
                      <input
                        type="number"
                        min="1"
                        className="form-control form-control-sm"
                        value={line.quantity}
                        onChange={(e) =>
                          updateLine(idx, "quantity", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control form-control-sm"
                        value={line.unitCost}
                        onChange={(e) =>
                          updateLine(idx, "unitCost", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control form-control-sm"
                        value={line.taxRate}
                        onChange={(e) =>
                          updateLine(idx, "taxRate", e.target.value)
                        }
                      />
                    </td>
                    <td className="fw-semibold small">
                      ₹{lineTotal.toFixed(2)}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-danger p-0"
                        onClick={() => removeLine(idx)}
                      >
                        <i className="ti ti-x" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary */}
      {formData.items.length > 0 && (
        <div className="row g-2 mb-3">
          <div className="col-md-6">
            <label className="form-label small">Notes</label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.notes}
              onChange={handleChange}
              name="notes"
            />
          </div>
          <div className="col-md-6">
            <div className="border rounded p-3 bg-light">
              <div className="d-flex justify-content-between small mb-1">
                <span>Subtotal:</span>
                <strong>₹{totals.subtotal.toFixed(2)}</strong>
              </div>
              <div className="d-flex justify-content-between small mb-1">
                <span>Tax:</span>
                <strong>₹{totals.taxAmount.toFixed(2)}</strong>
              </div>
              <div className="d-flex justify-content-between small mb-1 align-items-center">
                <span>Discount:</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control form-control-sm text-end"
                  style={{ width: 100 }}
                  name="discount"
                  value={formData.discount}
                  onChange={handleChange}
                />
              </div>
              <div className="d-flex justify-content-between small mb-2 align-items-center">
                <span>Shipping:</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control form-control-sm text-end"
                  style={{ width: 100 }}
                  name="shipping"
                  value={formData.shipping}
                  onChange={handleChange}
                />
              </div>
              <hr className="my-2" />
              <div className="d-flex justify-content-between fw-bold">
                <span>Grand Total:</span>
                <span>₹{totals.grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action toggle */}
      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="radio"
            id="actReceive"
            name="action"
            value="receive"
            checked={formData.action === "receive"}
            onChange={handleChange}
          />

          <label className="form-check-label" htmlFor="actReceive">
            <strong>Save & Receive</strong> — stock will update immediately
          </label>
        </div>

        <div className="form-check">
          <input
            className="form-check-input"
            type="radio"
            id="actDraft"
            name="action"
            value="draft"
            checked={formData.action === "draft"}
            onChange={handleChange}
          />

          <label className="form-check-label" htmlFor="actDraft">
            Save as Draft — stock will not update until you mark as received
          </label>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2 pt-2 border-top">
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canSave || loading}
          onClick={handleSave}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Saving...
            </>
          ) : (
            <>
              <i className="ti ti-check me-1" />{" "}
              {formData.action === "receive" ? "Save & Receive" : "Save Draft"}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
